import React, { memo, useMemo, useCallback, useState } from 'react';
import Table from '../../../../components/Table/Table';
import './MateList.css';

// Avatar component for user initials
const Avatar = memo(({ name, isAdmin }) => {
  const initials = name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : '?';
  const avatarClass = isAdmin ? 'avatar admin' : 'avatar user';
  
  return (
    <div className={avatarClass}>
      {initials}
    </div>
  );
});

Avatar.displayName = 'Avatar';

const MateList = memo(({ 
  mates = [], 
  loading = false, 
  onRowClick,
  className = '' 
}) => {
  const [roleFilter, setRoleFilter] = useState('all');

  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      weekday: 'short',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Define table columns
  const columns = useMemo(() => [
    {
      key: 'name',
      title: 'Navn',
      minWidth: '200px',
      render: (mate) => (
        <div className="name-content">
          <Avatar name={mate.name} isAdmin={mate.role === 'admin'} />
          <span className="name-text">{mate.name || 'Unnamed'}</span>
        </div>
      )
    },
    {
      key: 'email',
      title: 'E-mail adresse',
      minWidth: '200px',
      render: (mate) => (
        <span className="email-text">{mate.email}</span>
      )
    },
    {
      key: 'updatedAt',
      title: 'Senest opdateret',
      minWidth: '180px',
      render: (mate) => (
        <span className="date-text">{formatDate(mate.updatedAt || mate.createdAt)}</span>
      )
    }
  ], [formatDate]);

  // Define table filters
  const tableFilters = useMemo(() => (
    <>
      <select 
        className="filter-dropdown"
        value={roleFilter}
        onChange={(e) => setRoleFilter(e.target.value)}
      >
        <option value="all">Alle</option>
        <option value="admin">Admin Mates</option>
        <option value="user">Mates</option>
      </select>
    </>
  ), [roleFilter]);

  // Memoized filtered and sorted mates
  const filteredAndSortedMates = useMemo(() => {
    let filteredMates = mates;
    
    // Apply role filter
    if (roleFilter !== 'all') {
      filteredMates = mates.filter(mate => mate.role === roleFilter);
    }
    
    // Sort by admin status first, then by creation date
    return [...filteredMates].sort((a, b) => {
      if (a.role === 'admin' && b.role !== 'admin') return -1;
      if (a.role !== 'admin' && b.role === 'admin') return 1;
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [mates, roleFilter]);

  // Generate dynamic title with count
  const tableTitle = useMemo(() => {
    const count = filteredAndSortedMates.length;
    const totalCount = mates.length;
    
    if (roleFilter === 'all') {
      return `Antal: ${totalCount}`;
    } else if (roleFilter === 'admin') {
      return `Antal: ${count} af ${totalCount}`;
    } else {
      return `Antal: ${count} af ${totalCount}`;
    }
  }, [filteredAndSortedMates.length, mates.length, roleFilter]);

  const renderMobileCard = useCallback((mate) => (
    <div className="mate-mobile-name-only">
      <Avatar name={mate.name} isAdmin={mate.role === 'admin'} />
      <span className="name-text">{mate.name || 'Unnamed'}</span>
    </div>
  ), []);

  return (
    <div className={`mate-list ${className}`}>
      <Table
        data={filteredAndSortedMates}
        columns={columns}
        loading={loading}
        title={tableTitle}
        emptyMessage="Ingen brugere fundet"
        emptyDescription="Der er ingen registrerede brugere i systemet endnu."
        filters={tableFilters}
        onRowClick={onRowClick}
        defaultSort={{ key: 'createdAt', direction: 'desc' }}
        responsiveMode="cards"
        mobileCardRenderer={renderMobileCard}
      />
    </div>
  );
});

MateList.displayName = 'MateList';

export default MateList;
